import { useEffect, useMemo, useState } from "react";
import { adminFetch } from "./adminApi";

function Chip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/80">
      {children}
    </span>
  );
}

export default function AdminAudit() {
  const [q, setQ] = useState("");
  const [action, setAction] = useState("ALL");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const pageSize = 20;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (q.trim()) p.set("q", q.trim());
    if (action !== "ALL") p.set("action", action);
    return p.toString();
  }, [page, q, action]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/audit?${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load audit logs");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const canPrev = page > 1;
  const canNext = page * pageSize < total;

  // actions list (можно расширять)
  const ACTIONS = [
    "USER_BAN",
    "USER_UNBAN",
    "USER_INVALIDATE_TOKENS",
    "REQUEST_FORCE_STATUS",
    "BOOKING_FORCE_UPDATE",
    "WALLET_ADJUST",
  ];

  return (
    <div className="text-white">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-unbounded text-2xl font-bold">Audit Logs</h2>
          <p className="mt-2 text-white/70">
            Everything admins do: bans, force status, wallet adjustments, etc.
          </p>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search email/action/reason/id…"
            className="w-full md:w-72 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />

          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          >
            <option value="ALL">All actions</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>

          <button
            onClick={load}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-sm text-white/70">{loading ? "Loading…" : `Total: ${total}`}</div>
          <div className="flex items-center gap-2">
            <button
              disabled={!canPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
            >
              Prev
            </button>
            <Chip>Page {page}</Chip>
            <button
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
            >
              Next
            </button>
          </div>
        </div>

        {err && <div className="px-4 py-3 text-sm text-red-300">{err}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-white/60">
              <tr className="border-b border-white/10">
                <th className="px-4 py-3 text-left">When</th>
                <th className="px-4 py-3 text-left">Admin</th>
                <th className="px-4 py-3 text-left">Action</th>
                <th className="px-4 py-3 text-left">Target</th>
                <th className="px-4 py-3 text-left">Reason</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-white/60">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                items.map((x) => (
                  <tr key={x.id} className="border-b border-white/10 align-top">
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                      {new Date(x.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-white/80">{x.adminEmail}</td>
                    <td className="px-4 py-3">
                      <Chip>{x.action}</Chip>
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      <div className="space-y-1">
                        {x.targetUserId ? <div>user: <span className="text-white/80 break-all">{x.targetUserId}</span></div> : null}
                        {x.requestId ? <div>request: <span className="text-white/80 break-all">{x.requestId}</span></div> : null}
                        {x.bookingId ? <div>booking: <span className="text-white/80 break-all">{x.bookingId}</span></div> : null}
                        {x.walletTutorId ? <div>wallet: <span className="text-white/80 break-all">{x.walletTutorId}</span></div> : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/80">
                      <div className="whitespace-pre-wrap">{x.reason || "—"}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 text-xs text-white/50 border-t border-white/10">
          Note: before/after JSON is stored in DB (not shown here to keep UI clean).
        </div>
      </div>
    </div>
  );
}
